import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { API_DEFAULT_OPTIONS, ApiModuleConfig } from './api.module';

export type IApiObservable<TD = any, TM = any> = Observable<IApiModel<TD, TM>>;

export interface IApiModel<TD = any, TM = any> {
  data?: TD;
  meta?: TM;
}

const API_EDGE_USED_STORAGE_KEY = 'apiEdgeUsed';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    @Inject(API_DEFAULT_OPTIONS)
    private _defaultOptions: ApiModuleConfig,
  ) {}

  get options() {
    return this._defaultOptions;
  }

  getUrl(url: string) {
    return (
      this.options.apiUrl.replace(new RegExp('/$'), '') +
      '/' +
      url.replace(new RegExp('^/'), '')
    );
  }

  upload(
    path: string,
    data: { [key: string]: any },
    options: { [key: string]: any } = {},
  ): Observable<any> {
    const formData = new FormData();

    if (data) {
      Object.keys(data).forEach((key) => {
        const value = data[key];
        if (value instanceof FileList) {
          const length = value.length;
          if (length) {
            for (let i = 0; i < length; i++) {
              formData.append(key, value[i]);
            }
          }
        } else {
          formData.append(key, value);
        }
      });
    }

    return this.send('post', path, formData, options);
  }

  post(
    path: string,
    data?: { [key: string]: any },
    options: { [key: string]: any } = {},
  ): IApiObservable<any, any> {
    return this.send('post', path, data, options);
  }

  get(
    path: string,
    data?: { [key: string]: any },
    options: { [key: string]: any } = {},
  ): IApiObservable<any, any> {
    return this.send('get', path, data, options);
  }

  private send(
    method: string,
    path: string,
    data?: { [key: string]: any },
    options?: {
      [key: string]: any;
    },
  ): IApiObservable<any, any> {
    const finalOptions: any = {
      ...{
        withCredentials: true,
        headers: new HttpHeaders({ 'ngsw-bypass': 'true' }),
      },
      ...options,
    };

    let url = this._defaultOptions.apiUrl;
    let edgeIndex: number | null = null;
    if ('edge' in finalOptions && this._defaultOptions.apiEdgeUrl) {
      if (finalOptions.edge === true) {
        edgeIndex = this._getApiEdgeNext();
        this._setApiEdgeUse(edgeIndex);

        url = this._defaultOptions.apiEdgeUrl.replace(
          '{index}',
          String(edgeIndex),
        );
      }
      console.log(finalOptions.edge);
      delete finalOptions.edge;
    }

    let request;
    if (method === 'get') {
      if (data) {
        finalOptions.params = data;
      }
      request = this.http.get(url + '/' + path, finalOptions);
    } else {
      request = (this.http as any)[method](
        url + '/' + path,
        data,
        finalOptions,
      );
    }

    console.log('request', url, data, options);

    return request.pipe(
      tap(() => {
        if (edgeIndex !== null) {
          this._unsetApiEdgeUse(edgeIndex);
        }
      }),
      catchError((response: HttpErrorResponse) => {
        const result = response.error;
        if (result?.class && result?.class.endsWith('ApiException')) {
          console.error('apiError', result);
          return throwError(result);
        } else {
          console.error('error', result);
          this._presentErrorMessage(result);
        }
        return EMPTY;
      }),
    );
  }

  private _presentErrorMessage(e: string) {
    console.error('parseError', e);
  }

  private _getApiEdgeNext(): number {
    const result = Object.values(this._getApiEdgeUsed());
    const sortedResult = [...result];

    (sortedResult as any).sort((a: number, b: number) => a - b);

    return result.indexOf(sortedResult[0]);
  }

  private _getApiEdgeUsed() {
    const storageResult = localStorage.getItem(API_EDGE_USED_STORAGE_KEY);
    if (storageResult) {
      //return JSON.parse(storageResult);
    }

    const result: any = {};
    for (let i = 0; i < (this.options.apiEdgeMax || 1); i++) {
      result[i] = 0;
    }

    return result;
  }

  private _setApiEdgeUse(index: number, value: number | null = null) {
    const result = this._getApiEdgeUsed();
    if (value === null) {
      result[index] = DateTime.now().valueOf();
    } else {
      result[index] = value;
    }

    localStorage.setItem(API_EDGE_USED_STORAGE_KEY, JSON.stringify(result));
  }

  private _unsetApiEdgeUse(index: number) {
    this._setApiEdgeUse(index, 0);
  }
}
